// Email Verification Completion Page
// Handles redirect after email verification to complete the flow

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User as FirebaseUser } from 'firebase/auth';
import { useAuthContext } from '../contexts/AuthContext';
import { authenticatedApi } from '../lib/authenticated-api';

/**
 * Apply any subscription grant that was waiting on this address being proved,
 * and make the result visible to this client (#1883).
 *
 * A subscription bought through a partner is matched to us on email address,
 * and the entitlement is withheld until we have seen the address proved. Doing
 * that proving raises no event our backend can act on — completing
 * verification on Firebase's hosted page updates the Firebase Auth record and
 * fires no trigger — so this page, reached through that page's Continue once
 * the address is verified, is the earliest anything can tell us.
 *
 * **Every failure is swallowed here, and that placement is the point.** The
 * caller's `catch` shows "Verification Pending" and sends the user to
 * /verify-email, and `authenticatedApi` throws on any non-2xx — so letting a
 * 429 or a dropped connection escape would take somebody whose address IS
 * proved and tell them it is not. A missed reconcile costs them a wait; that
 * would cost them the page.
 *
 * The wait is real, though, so it is worth naming: nothing here retries, and if
 * this call is lost the grant waits for the next boundary that reconciles —
 * their next sign-in, their next visit to a connected application, or their
 * next trip to HeapsterLive.
 */
async function reconcileEntitlements(
  firebaseUser: FirebaseUser
): Promise<void> {
  let applied = false;

  try {
    const response = await authenticatedApi.request<{
      status: string;
      applied: boolean;
    }>('/v1/auth/reconcile-entitlements', { method: 'POST' });

    // Custom claims are the authorization mechanism, and they reach this client
    // only when it refreshes its ID token. `applied` says an apply ran, so the
    // token this page is holding is now out of date.
    //
    // Awaited before the caller navigates, deliberately: this updates the SDK's
    // cached token, so the API calls the next page makes carry the new claims.
    // Fire it without awaiting and the redirect can win, leaving those calls on
    // the old token until it expires.
    //
    // This line is load-bearing, so do not "simplify" it away. Entitlements are
    // read from ID-TOKEN CLAIMS, not from an API: `useEntitlements` calls
    // `getIdTokenResult()` unforced, and `ProtectedRoute` gates on the result.
    // Unforced means it returns the SDK's cached token — the one this call has
    // just replaced. Remove the refresh and the next page evaluates its
    // entitlement guard against claims written before the grant existed.
    //
    // What it does NOT do is re-render React state. `AuthContext` subscribes to
    // `onAuthStateChanged`, which does not fire on a token refresh, so anything
    // reading claims out of context rather than off the token stays stale until
    // a reload or a fresh sign-in. Nothing does today; it is a sharp edge rather
    // than a live bug, and switching that listener would be its own change.
    //
    // `data` is `T | null` by design (a 204 carries no body), hence `?.`.
    applied = response.data?.applied === true;
  } catch (error) {
    console.warn(
      'Entitlement reconcile failed; it will be applied at the next sign-in',
      error
    );
    return;
  }

  if (!applied) return;

  try {
    await firebaseUser.getIdToken(true);
  } catch (error) {
    // Distinct from the failure above, and worth saying so: the grant IS
    // applied. Only this client's view of it is stale, and its own token
    // refresh will catch up within the hour.
    console.warn(
      'Grant applied, but this client could not refresh its token to see it',
      error
    );
  }
}

export function AuthVerifyCompletePage() {
  const navigate = useNavigate();
  const { firebaseUser, user, isLoading, refreshUserProfile } =
    useAuthContext();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>(
    'verifying'
  );

  // Read through a ref rather than depended on. `refreshUserProfile` is
  // redefined on every provider render, and this effect POSTs, refreshes a token
  // and starts a redirect timer — in the deps it would restart all of that on
  // each render, including the one caused by the profile refresh this effect
  // itself triggers. `user` is `useState` state and so does not churn the same
  // way; it rides along because the two are read together and it is the value
  // the redirect below branches on.
  //
  // Assigned in a layout effect rather than in the render body: React may yield,
  // pause or discard a render, and a ref written during one keeps a value from a
  // render that never committed. Layout effects run before passive ones, so the
  // effect below always reads a committed value.
  const latest = useRef({ user, refreshUserProfile });
  useLayoutEffect(() => {
    latest.current = { user, refreshUserProfile };
  }, [user, refreshUserProfile]);

  useEffect(() => {
    // Wait for auth to resolve. `firebaseUser` is null on the first render for
    // EVERY visitor, signed in or not, so acting on it here would send someone
    // with a perfectly good session down the signed-out path.
    if (isLoading) return;

    // No "already ran" latch here, deliberately. One would pair badly with the
    // cleanup below: StrictMode unmounts and remounts, so the first run would be
    // cancelled and the second skipped, and the page would sit on its spinner
    // for ever in development. A ref survives that remount; the work must not
    // depend on it.
    //
    // Re-running is safe instead of prevented: the reconcile is idempotent and
    // the server rate-limits per uid, so a repeat costs a redundant apply rather
    // than a wrong one. The cleanup below suppresses the previous run's UI
    // outcome — it does not abort a request already in flight, and the server
    // work completes either way.

    // Both redirects are deferred so the outcome is readable before the page
    // changes under them, which means both can outlive the component. Without
    // the cleanup below, someone who clicks away during those seconds is yanked
    // to the dashboard from wherever they went.
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const continueToApp = () => {
      if (cancelled) return;
      setStatus('success');
      timer = setTimeout(() => {
        if (latest.current.user?.profile_completed) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/complete-profile', { replace: true });
        }
      }, 2000);
    };

    const giveUp = () => {
      if (cancelled) return;
      setStatus('error');
      timer = setTimeout(() => {
        navigate('/verify-email', { replace: true });
      }, 3000);
    };

    const handleVerification = async () => {
      try {
        if (!firebaseUser) {
          // No session in THIS browser, which is ordinary rather than an error:
          // people read mail on their phone. Firebase applied the action code
          // before sending them here, so the address is proved — they simply
          // cannot be signed in from here. Signing in anywhere applies any
          // waiting grant on its own.
          if (!cancelled) setStatus('success');
          return;
        }

        // Firebase applied the code before redirecting, but this client's cached
        // record can predate that. `reload()` is what refreshes
        // `emailVerified`; a forced `getIdToken` does NOT touch it, which is why
        // the retry this replaces could never succeed.
        //
        // Its own catch, for the same reason the reconcile has one: a network
        // blip here must not be reported as "not verified" to somebody Firebase
        // has already verified. Falling through on failure is safe — the check
        // below still refuses to continue without evidence, and if the cached
        // record already said verified, that evidence has not gone anywhere.
        const wasVerified = firebaseUser.emailVerified;
        if (!wasVerified) {
          try {
            await firebaseUser.reload();
          } catch (error) {
            console.warn('Could not refresh the verification state', error);
          }
        }

        if (!firebaseUser.emailVerified) {
          giveUp();
          return;
        }

        // `reload()` mutates this User in place, and that is not enough on its
        // own. `AuthContext` computes `isEmailVerified` from this same object in
        // its render body and only re-renders on `onAuthStateChanged`, which a
        // reload does not fire — so the provider would still be holding `false`
        // while we send them to a route guarded on it, and `VerifiedRoute` would
        // bounce them straight to /verify-email. Having just told them "Email
        // Verified!", that is the worst possible landing.
        //
        // `refreshUserProfile` calls `setUser`, which re-renders the provider
        // and recomputes the flag. It also refreshes `profile_completed`, which
        // the redirect below branches on.
        if (!wasVerified) {
          try {
            await latest.current.refreshUserProfile();
          } catch (error) {
            console.warn('Could not refresh the cached profile', error);
          }
        }

        // Never throws — see the note on the function.
        await reconcileEntitlements(firebaseUser);
        continueToApp();
      } catch (error) {
        console.error('Error handling verification:', error);
        giveUp();
      }
    };

    handleVerification();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, firebaseUser, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <svg
                  className="animate-spin h-6 w-6 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
                Verifying Your Email
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Please wait while we complete the verification process...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-green-900 leading-tight">
                Email Verified!
              </h1>
              {firebaseUser ? (
                <p className="mt-2 text-sm sm:text-base text-gray-600">
                  Your email has been successfully verified. Redirecting you to
                  the dashboard...
                </p>
              ) : (
                <>
                  <p className="mt-2 text-sm sm:text-base text-gray-600">
                    Your email has been successfully verified! Please log in to
                    continue.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() => navigate('/signin')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Log In
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-yellow-900 leading-tight">
                Verification Pending
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Email verification may still be processing. Taking you back to
                the verification page...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
